import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton } from '@mui/material';

import { isLoading } from 'hooks/requests';
import PurchaseTable from './PurchaseTable';
import PurchaseModal from './PurchasesModal';
import {
    payemntPlanSchema,
    payemntPlanInitialValues,
} from 'components/Inventory/utils/paymentPlanSchema';
import { useThemeToggle } from 'hooks/useThemeToggle';
import { Button, TextField, Typography } from 'shared';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import ScheduledPaymentModal from './ScheduledPaymentModal';
import ScheduledPaymentsList from './ScheduledPaymentsList';
import { notesText } from 'components/Inventory/style/style';
import { paymentPlanFormStyles } from 'components/Inventory/style/style';
import { usePaymentPlanService } from 'store/services/paymentPlan.service';

const PaymentPlansForm = ({
    type,
    initialValues,
    setAlertData,
    handlePaymentPlanSuccess,
    handlePaymentPlanFormCancel,
}) => {
    const { themeMode } = useThemeToggle();

    const classes = paymentPlanFormStyles();
    const companyData = useSelector((state) => state.company.data);
    const { createPaymentPlan, updatePaymentPlan } = usePaymentPlanService();

    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showScheduledPaymentForm, setShowScheduledPaymentForm] = useState(false);
    const [purchaseData, setPurchaseData] = useState(initialValues?.purchaseData || []);

    const purchases = parseFloat(initialValues?.total ?? 0);
    const applied = parseFloat(initialValues?.balance ?? 0);
    const balance = purchases - applied;

    const formatDate = (isoString) => dayjs(isoString).format('M/D/YYYY');

    const handleSubmit = async (values) => {
        try {
            const payload = {
                name: values.name,
                notes: values.notes,
                scheduledPayments: values.scheduledPayments,
                purchaseData: purchaseData.map((p) => p.id),
                companyId: companyData?.id,
            };

            if (type === 'Edit' && initialValues?.paymentPlanId) {
                payload.paymentPlanId = initialValues.paymentPlanId;
                const response = await updatePaymentPlan(payload);
                handlePaymentPlanSuccess(response.message);
            } else {
                const response = await createPaymentPlan({
                    ...payload,
                });
                handlePaymentPlanSuccess(response.message);
            }
        } catch (error) {
            console.log("this is error to see", error)
            handleErrorAlert(setAlertData);
        }
    };

    const handlePurchaseModalSubmit = (data) => {
        setPurchaseData(data);
        setShowPurchaseModal(false);
    };

    useEffect(() => {
        if (initialValues?.purchaseData) {
            setPurchaseData(initialValues.purchaseData);
        }
    }, [initialValues]);

    return (
        <Box sx={classes.rootWrapper}>
            <Box sx={classes.headingWrapper}>
                <Typography fontWeight="bold" fontSize="20px" text={`${type} Payment Plan`} />
                <IconButton>
                    <CloseIcon onClick={handlePaymentPlanFormCancel} />
                </IconButton>
            </Box>
            <Formik
                initialValues={
                    initialValues
                        ? payemntPlanInitialValues(initialValues)
                        : payemntPlanInitialValues({})
                }
                enableReinitialize={true}
                validationSchema={payemntPlanSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            name="name"
                                            placeholder="Name"
                                            inlineLabel="Name"
                                            value={values.name}
                                            onChange={(e) => {
                                                setFieldValue('name', e.target.value);
                                                if (e.target.value.length > 150) {
                                                    setAlertData({
                                                        severity: 'error',
                                                        message: 'Name cannot be more than 150 characters',
                                                        open: true,
                                                    });
                                                }
                                            }}
                                            error={touched.name && !!errors.name}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            name="notes"
                                            placeholder="Notes"
                                            inlineLabel="Notes"
                                            value={values.notes}
                                            onChange={(e) => setFieldValue('notes', e.target.value)}
                                            error={touched.notes && !!errors.notes}
                                            helperText={touched.notes && errors.notes}
                                            multiline
                                            minRows={2}
                                            InputProps={{
                                                style: notesText(themeMode),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Box sx={classes.totalCalcBox}>
                                        {[
                                            {
                                                label: 'Total Scheduled Payments',
                                                value: `$${purchases.toFixed(2)}`,
                                            },
                                            { label: 'Paid', value: `$${applied.toFixed(2)}` },
                                            {
                                                label: 'Remaining Balance',
                                                value: `$${balance.toFixed(2)}`,
                                                isBold: true,
                                            },
                                        ].map(({ label, value }, index) => (
                                            <Box
                                                key={label}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                paddingY={index === 2 ? '8px' : '4px'}
                                                borderTop={index === 2 ? '1px solid #ccc' : 'none'}
                                                marginTop={index === 2 ? '8px' : '0'}
                                            >
                                                <Typography
                                                    sx={classes.totalCalcBoxText}
                                                    text={label}
                                                />
                                                <Typography
                                                    sx={classes.totalCalcBoxValue}
                                                    text={value}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={classes.scheduledPaymentsHeader}>
                                    <Typography text="Scheduled Payments" />
                                    <Button
                                        text="Add Scheduled Payment"
                                        startIcon={<AddIcon />}
                                        customStyles={{ padding: '10px' }}
                                        onClick={() => setShowScheduledPaymentForm(true)}
                                    />
                                </Box>

                                <ScheduledPaymentsList
                                    errors={errors}
                                    values={values}
                                    touched={touched}
                                    setFieldValue={setFieldValue}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box marginTop="10px">
                                    <Typography text="MATCHED PAYMENTS" />
                                </Box>

                                {initialValues?.plaidTransactions?.length > 0 ? (
                                    <Box marginTop="20px">
                                        {initialValues.plaidTransactions.map((txn, idx) => (
                                            <Box
                                                key={idx}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={classes.matchedTxnBox}
                                            >
                                                <Typography
                                                    sx={classes.matchedTxnDate}
                                                    text={formatDate(txn.date)}
                                                />
                                                <Typography
                                                    sx={classes.matchedTxnDesc}
                                                    text={txn.description || 'No description'}
                                                />
                                                <Typography
                                                    sx={classes.matchedTxnAmount}
                                                    text={`$${Math.abs(
                                                        parseFloat(txn.amount),
                                                    ).toFixed(2)}`}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box marginTop="25px">
                                        <Typography text="There are no bank transactions linked to this payment plan." />
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}></Grid>
                            <Grid item xs={12} md={6}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        text="Add Purchases"
                                        startIcon={<AddIcon />}
                                        customStyles={{ padding: '10px' }}
                                        onClick={() => setShowPurchaseModal(true)}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <PurchaseTable
                                    data={purchaseData}
                                    handleDelete={(id) => {
                                        setPurchaseData((prev) =>
                                            prev.filter((item) => item.id !== id),
                                        );
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        loading={isLoading()}
                                        text={type.toLowerCase() === 'create' ? 'Create' : 'Update'}
                                        type="submit"
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        {showScheduledPaymentForm && (
                            <ScheduledPaymentModal
                                open={showScheduledPaymentForm}
                                onClose={() => setShowScheduledPaymentForm(false)}
                                onAddPayments={(payments) =>
                                    setFieldValue('scheduledPayments', [
                                        ...values.scheduledPayments,
                                        ...payments,
                                    ])
                                }
                            />
                        )}

                        {showPurchaseModal && (
                            <PurchaseModal
                                purchaseData={purchaseData}
                                showPurchaseModal={showPurchaseModal}
                                setShowPurchaseModal={setShowPurchaseModal}
                                handlePurchaseModalSubmit={handlePurchaseModalSubmit}
                            />
                        )}
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default PaymentPlansForm;
